import { useState } from 'react';
import { Link } from 'react-router-dom';
import { trendingList } from '@/mocks/trending';

type Props = {
  onSearch: (query: string) => void;
};

const quickTags = ['片场花絮', '音乐节返场', '综艺路透', '品牌联名', '粉丝应援', '剧集收官'];

export default function Hero({ onSearch }: Props) {
  const [value, setValue] = useState('');
  const topFive = trendingList.slice(0, 5);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    onSearch(value.trim());
  };

  return (
    <section className="relative w-full overflow-hidden bg-foreground-950 pt-16">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20cinematic%20background%20with%20warm%20vermillion%20red%20and%20amber%20gold%20light%20streaks%2C%20soft%20glowing%20bokeh%20particles%2C%20deep%20charcoal%20tones%2C%20artistic%20gradient%20texture%2C%20no%20readable%20text&width=1800&height=1000&seq=gualiao-hero-01&orientation=landscape"
          alt="今日娱乐热点聚合的抽象氛围背景"
          title="黑料今日 · 今日热点事件与热门话题聚合"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/85 via-foreground-950/75 to-foreground-950/92"></div>
      </div>

      <div className="relative w-full px-4 md:px-8 lg:px-12 pt-14 md:pt-20 pb-10 md:pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-10 lg:gap-14 items-start">
          <div className="animate-rise">
            <p className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 border border-background-50/20 text-xs text-accent-300 mb-6">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-fire-fill animate-soft-pulse" aria-hidden="true"></i>
              </span>
              今日更新 · 共收录 {trendingList.length} 条热点话题
            </p>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-[3.4rem] font-black leading-tight md:leading-[1.15] text-background-50 max-w-3xl">
              黑料今日
              <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-accent-300 via-primary-300 to-accent-400">
                今日热点事件与热门话题聚合
              </span>
            </h1>
            <p className="mt-6 text-sm md:text-base text-background-100/80 leading-7 max-w-2xl">
              这里把今日热点事件与热门话题聚合好，热点盘点、热议焦点都放在同一页。想快速知道今天哪个瓜被拿得多，
              点进来扫一遍就能看到值得吃的几件事，省掉好多处乱逛的时间。
            </p>

            <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl" role="search">
              <div className="flex-1 flex items-center gap-2 bg-background-50 rounded-full pl-4 pr-2 h-12">
                <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                  <i className="ri-search-line" aria-hidden="true"></i>
                </span>
                <input
                  type="search"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="搜索话题、艺人、剧集或事件关键词"
                  aria-label="搜索今日热点话题"
                  className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                className="h-12 px-6 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-fire-line" aria-hidden="true"></i>
                扫一遍今日热点
              </button>
            </form>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-xs text-background-100/60 mr-1">热门标签</span>
              {quickTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => {
                    setValue(tag);
                    onSearch(tag);
                  }}
                  className="px-3 py-1 rounded-full border border-background-50/25 text-xs text-background-100/90 hover:border-accent-400 hover:text-accent-300 transition-colors whitespace-nowrap"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <aside className="bg-background-50/8 border border-background-50/15 rounded-lg backdrop-blur-sm p-5 md:p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-lg font-bold text-background-50 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-primary-400">
                  <i className="ri-live-line" aria-hidden="true"></i>
                </span>
                实时热搜速览
              </h2>
              <Link
                to="/ranking"
                className="text-xs text-accent-300 hover:text-accent-200 transition-colors whitespace-nowrap flex items-center gap-1"
              >
                完整榜单
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
            <ol className="space-y-3">
              {topFive.map((item) => (
                <li key={item.rank}>
                  <Link
                    to={item.articleId ? `/article/${item.articleId}` : '/ranking'}
                    className="flex items-start gap-3 group"
                  >
                    <span
                      className={`w-6 h-6 shrink-0 rounded-md flex items-center justify-center text-xs font-bold ${
                        item.rank <= 3
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-50/15 text-background-100'
                      }`}
                    >
                      {item.rank}
                    </span>
                    <span className="flex-1">
                      <span className="block text-sm text-background-100 group-hover:text-accent-300 transition-colors line-clamp-2 leading-6">
                        {item.title}
                      </span>
                      <span className="mt-1 flex items-center gap-2 text-[11px] text-background-100/50">
                        <span>{item.category}</span>
                        <span className="flex items-center gap-0.5">
                          <i className="ri-fire-line" aria-hidden="true"></i>
                          {item.heat.toLocaleString()}
                        </span>
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </aside>
        </div>
      </div>

      <div className="relative border-t border-background-50/15 bg-foreground-950/60 py-3 overflow-hidden">
        <div className="flex items-center gap-4 px-4 md:px-8">
          <span className="shrink-0 px-2.5 py-1 rounded bg-primary-500 text-background-50 text-xs whitespace-nowrap">
            滚动速报
          </span>
          <div className="overflow-hidden flex-1">
            <div className="flex gap-8 whitespace-nowrap animate-ticker">
              {[...trendingList, ...trendingList].map((item, index) => (
                <span key={`${item.rank}-${index}`} className="text-xs text-background-100/70 flex items-center gap-2">
                  <i className="ri-flashlight-line text-accent-400" aria-hidden="true"></i>
                  {item.title}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}